import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import './index.css';

/**
 * Service-worker registration with updateViaCache: 'none'.
 *
 * Without this flag, browsers honor HTTP caching when checking /sw.js
 * for updates. Our origin sets Cache-Control: max-age=0 on /sw.js, but
 * Cloudflare in front of Netlify rewrites that to max-age=14400
 * (4 hours), so users keep using their old SW + old precached bundle
 * for four hours after every deploy. updateViaCache: 'none' bypasses
 * the HTTP cache for the SW script specifically, so a new deploy
 * propagates within seconds.
 *
 * vite-plugin-pwa's auto-injected register doesn't expose this flag,
 * so we set injectRegister: false in vite.config.ts and do it here.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch((err) => {
        // Non-fatal — app still functions without offline cache.
        // eslint-disable-next-line no-console
        console.warn('[sw] registration failed', err);
      });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
